import { defineStore } from "pinia";
import { AnalyticsV2ElementType } from "@/models/etc/types";
import CommonSelector from "@/models/base/common.selector";
import { reactive } from "vue";
import { generalStore } from "./general";
import { storageStore } from "./storage/storage-instances";

/**
 * global segment and authentication store
 */

const API_URL = process.env.VUE_APP_API_URL

export type RootState = {
    jwtToken: string;
    mode: number;
    generalSelector: CommonSelector[];

    // for old API
    sessionKey: string;
    userID: string;
};

/**
 * elementName здесь — КЛЮЧ i18n, а не готовый перевод. Селектор собирается по правам
 * доступа (в setJWT) и живёт в state строками, а язык приходит из профиля пользователя
 * позже — если переводить заранее, названия сегментов навсегда застывали бы на локали,
 * активной в момент сборки. Перевод делает потребитель при отрисовке (segment.selector),
 * поэтому названия следуют за языком сами.
 */
function buildGeneralSelector(canReadStorage: boolean, canReadWorkload: boolean): CommonSelector[] {
  const selector: CommonSelector[] = [
    new CommonSelector(AnalyticsV2ElementType.Project, 'projects_label'),
    new CommonSelector(AnalyticsV2ElementType.Client, 'clients_label'),
    new CommonSelector(AnalyticsV2ElementType.Job, 'jobs_label'),
    new CommonSelector(AnalyticsV2ElementType.Person, 'contacts_label'),
    new CommonSelector(AnalyticsV2ElementType.File, 'files_label'),
    new CommonSelector(AnalyticsV2ElementType.Tag, 'tags_upcase'),
  ];

  if (canReadWorkload) {
    selector.push(new CommonSelector(AnalyticsV2ElementType.Workload, 'load_upcase'));
  }

  // Storage segments are always at the bottom of regular segments.
  if (canReadStorage) {
    selector.push(
      new CommonSelector(AnalyticsV2ElementType.StorageItem, 'segment_storage_positions'),
      new CommonSelector(AnalyticsV2ElementType.StorageItemMovement, 'segment_storage_actions'),
    );
  }

  selector.push(new CommonSelector(AnalyticsV2ElementType.Report, 'reports_label'));
  return selector;
}

export const rootStore = defineStore("rootStore", {
  state: () =>
    ({
        jwtToken: '',
        mode: 0,
        sessionKey: '',
        userID: '',
        generalSelector: reactive(buildGeneralSelector(true, true) as CommonSelector[])
    } as RootState),

  actions: {
    // set auth token got from the main client
    async setJWT(payload: string, skey: string, uid: string) {
      this.jwtToken = payload

      this.sessionKey = skey;
      this.userID = uid;

      // check read/write access rights
      const genstore = generalStore()
      const storage = storageStore()

      await genstore.checkGeneralAccess()
      await storage.getStorageAccess()

      console.log('wload access: '+genstore.accessRights.AccessToWorkload)
      console.log('store access: '+storage.storageAccess?.CanRead)

      this.$state.generalSelector = reactive(buildGeneralSelector(
        !!storage.storageAccess?.CanRead,
        !!genstore.accessRights.AccessToWorkload,
      ) as CommonSelector[])
    },

    setToken(payload: string) {
      this.jwtToken = payload
    },

    // global segment (same as Destination)
    setGeneralMode(payload: number) {
      this.mode = payload
    },
  },

  getters: {
    // jwt auth token
    globalJWT(state): string {
      return state.jwtToken
    },

    // jwt auth token
    oldAPISessionKey(state): string {
      return state.sessionKey
    },

    // jwt auth token
    oldAPIUID(state): string {
      return state.userID
    },

    // global segment getter
    generalMode(state): number {
      return state.mode
    }
  }
});
